import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { AlarmClock, ArrowRight, BellOff, Bot, Check, ClipboardList, PenLine, Sunrise } from "lucide-react";
import {
  notificationCentreApi,
  SNOOZE_CHOICES,
  type Notice,
  type NoticeAction,
  type NoticeGroup,
  type SnoozeOption
} from "../../api/notificationCentreApi";
import { openViperLauncher } from "../viper/viperStore";
import { formatAgo, formatDateTimeShort } from "../../lib/format";
import { offerMarkReadUndo, offerSnoozeUndo } from "./noticeUndo";
import { VIPER_ACTION_KIND, ViperActionControls } from "./ViperActionControls";

// Release 1.5.0 notification centre (O-R55): notices grouped by work order or topic. Every action opens a page; a person
// signs on that page, never here. Opening a notice marks it read; snooze hides it until a plant time.

const ACTION_ICONS = {
  sign: PenLine,
  open: ArrowRight,
  "work-order": ClipboardList,
  viper: Bot
} as const;

interface NoticeGroupsProps {
  groups: NoticeGroup[];
  /** The bell's panel: fewer words, the first action only, at most `maxPerGroup` notices per group. */
  compact?: boolean;
  maxPerGroup?: number;
  /** Called after anything changed on the server (read, snooze). */
  onChanged: () => void;
  /** Called when the person follows a link (the bell closes its panel). */
  onNavigate?: () => void;
}

export function NoticeGroups({ groups, compact = false, maxPerGroup, onChanged, onNavigate }: NoticeGroupsProps) {
  return (
    <ul className="flex flex-col gap-3" data-testid="notice-groups">
      {groups.map((group) => {
        const shown = maxPerGroup ? group.notices.slice(0, maxPerGroup) : group.notices;
        const more = group.notices.length - shown.length;
        return (
          <li key={group.key} data-testid={`notice-group-${group.key}`}>
            <div className="mb-1 flex items-center justify-between gap-2">
              <h3 className="flex min-w-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                {group.groupKind === "work-order" ? <ClipboardList aria-hidden="true" className="h-3.5 w-3.5 flex-none" /> : null}
                <span className="truncate">{group.label}</span>
              </h3>
              {group.unreadCount > 0 && (
                <span
                  className="flex-none rounded-full border border-brand-300 bg-white px-1.5 text-[11px] font-semibold text-brand-700"
                  data-testid="group-unread"
                >
                  {group.unreadCount} new
                </span>
              )}
            </div>
            <ul className="flex flex-col gap-1.5">
              {shown.map((notice) => (
                <NoticeRow key={notice.id} notice={notice} compact={compact} onChanged={onChanged} onNavigate={onNavigate} />
              ))}
            </ul>
            {more > 0 && <p className="mt-1 text-[11px] text-slate-600">and {more} more in this group</p>}
          </li>
        );
      })}
    </ul>
  );
}

interface NoticeRowProps {
  notice: Notice;
  compact: boolean;
  onChanged: () => void;
  onNavigate?: () => void;
}

function NoticeRow({ notice, compact, onChanged, onNavigate }: NoticeRowProps) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const menuId = useId();
  const unread = !notice.isRead && !notice.snoozedUntilUtc;
  const actions = compact ? notice.actions.slice(0, 1) : notice.actions;

  // Opening a notice marks it read (O-R55); nothing else is changed. The explicit "Mark read" button offers Undo (O-R67).
  const markRead = (withUndo = false) => {
    if (notice.isRead) return;
    void notificationCentreApi
      .markRead([notice.id])
      .then(() => {
        onChanged();
        if (withUndo) offerMarkReadUndo(notice, onChanged);
      })
      .catch(() => undefined);
  };

  const snooze = async (option: SnoozeOption) => {
    setBusy(true);
    try {
      const res = await notificationCentreApi.snooze([notice.id], option);
      setStatus(res.message);
      setSnoozeOpen(false);
      onChanged();
      if (option !== "wake") offerSnoozeUndo(notice, res.message, onChanged);
    } catch {
      setStatus("Could not snooze it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={`rounded-lg border px-3 py-2 ${unread ? "border-brand-300 bg-brand-50" : "border-slate-200 bg-white"}`}
      data-testid={`notice-${notice.id}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
        <p className="min-w-0 break-words text-sm font-medium text-slate-900">
          {unread && (
            <span className="mr-1.5 rounded border border-brand-300 bg-white px-1 py-px text-[10px] font-semibold uppercase text-brand-700">
              New
            </span>
          )}
          {notice.title}
        </p>
        <p className="text-[11px] text-slate-600" title={formatDateTimeShort(notice.createdAtUtc)}>
          {compact ? formatAgo(notice.createdAtUtc) : `${notice.typeLabel} · ${formatDateTimeShort(notice.createdAtUtc)}`}
        </p>
      </div>
      {!compact && <p className="mt-0.5 break-words text-sm leading-relaxed text-slate-600">{notice.body}</p>}
      {notice.snoozedUntilUtc && (
        <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-600">
          <AlarmClock aria-hidden="true" className="h-3 w-3" /> Snoozed until {formatDateTimeShort(notice.snoozedUntilUtc)}
        </p>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {actions.map((action) => (
          <NoticeActionButton
            key={`${action.kind}-${action.label}`}
            action={action}
            onUse={() => {
              markRead();
              onNavigate?.();
            }}
          />
        ))}
        {notice.snoozedUntilUtc ? (
          <button
            type="button"
            className="btn-secondary !px-2 !py-0.5 text-xs"
            disabled={busy}
            onClick={() => void snooze("wake")}
            data-testid={`wake-${notice.id}`}
          >
            <Sunrise aria-hidden="true" className="mr-1 inline h-3 w-3" />
            Show it now
          </button>
        ) : (
          <div className="relative">
            <button
              type="button"
              className="btn-secondary !px-2 !py-0.5 text-xs"
              aria-expanded={snoozeOpen}
              aria-controls={menuId}
              disabled={busy}
              onClick={() => setSnoozeOpen((v) => !v)}
              data-testid={`snooze-${notice.id}`}
            >
              <BellOff aria-hidden="true" className="mr-1 inline h-3 w-3" />
              Snooze
            </button>
            {snoozeOpen && (
              <ul id={menuId} className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
                {SNOOZE_CHOICES.map((c) => (
                  <li key={c.option}>
                    <button
                      type="button"
                      className="w-full rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
                      onClick={() => void snooze(c.option)}
                      data-testid={`snooze-${notice.id}-${c.option}`}
                    >
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {!compact && !notice.isRead && (
          <button type="button" className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => markRead(true)} data-testid={`mark-read-${notice.id}`}>
            <Check aria-hidden="true" className="mr-1 inline h-3 w-3" />
            Mark read
          </button>
        )}
      </div>
      {!compact && notice.kind === VIPER_ACTION_KIND && <ViperActionControls noticeId={notice.id} onChanged={onChanged} onNavigate={onNavigate} />}
      {status && (
        <p role="status" className="mt-1 text-[11px] text-slate-600">
          {status}
        </p>
      )}
    </li>
  );
}

function NoticeActionButton({ action, onUse }: { action: NoticeAction; onUse: () => void }) {
  const Icon = ACTION_ICONS[action.kind] ?? ArrowRight;
  const className =
    action.kind === "sign"
      ? "btn-primary inline-flex items-center gap-1 !px-2 !py-0.5 text-xs"
      : "btn-secondary inline-flex items-center gap-1 !px-2 !py-0.5 text-xs";
  if (action.kind === "viper") {
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          onUse();
          openViperLauncher(action.question ?? "");
        }}
        data-testid="notice-action-viper"
      >
        <Icon aria-hidden="true" className="h-3 w-3" />
        {action.label}
      </button>
    );
  }

  if (!action.href) return null;
  return (
    <Link to={action.href} className={className} onClick={onUse} data-testid={`notice-action-${action.kind}`}>
      <Icon aria-hidden="true" className="h-3 w-3" />
      {action.label}
    </Link>
  );
}
